import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { Send, Sparkles, User, Bot, Mail, Download } from "lucide-react";
import { professionalData } from "./professionalData";
import { projectsData } from "./projectsData";
import Groq from "groq-sdk";
import { getMeemoSystemPrompt } from "../utils/prompts";
import emailjs from "@emailjs/browser";

const AskBotPage = ({ nightMode, setNightMode }) => {
  const [messages, setMessages] = useState([
    {
      type: "bot",
      content: "Hey! I'm Meemo, your friendly assistant who knows all about Surmayee. Ask me anything about her work, projects, or what she's great at!",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactDetails, setContactDetails] = useState({ name: "", email: "", message: "" });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize Groq client
  const groqClient = new Groq({
    apiKey: "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",
    dangerouslyAllowBrowser: true // Note: In production, use environment variables
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    // Only scroll to bottom when new messages are added (not on initial mount)
    if (messages.length > 1) {
      scrollToBottom();
    }
  }, [messages]);

  // Knowledge base about Surmayee
  const knowledgeBase = {
    name: "Surmayee Umathe",
    title: "Luxury Management & Marketing Expert",
    summary: "I'm someone who reads brand reports like novels and novels like brand reports. I see stories in packaging, poetry in retail, and culture hidden in campaigns. I'm fluent in two languages: strategy and sentiment. My work lives in the space between decoding how culture, desire, and detail create what we call luxury.",

    expertise: [
      "Luxury Buying & Merchandising Strategy",
      "Brand Positioning & Storytelling",
      "Consumer Insight & Market Research",
      "Cultural Foresight & Experience Design",
      "Visual Merchandising",
      "Retail Strategy",
      "Product Development",
      "Launch Coordination",
      "Vendor & Stakeholder Management",
    ],

    professional: professionalData,
    projects: projectsData,

    hobbies: [
      { name: "Painting", description: "Canvas work, studying old masters, precision in color" },
      { name: "Photography", description: "Studied for a year in college, trained eye for composition and emotion" },
      { name: "Reading", description: "Novels, fashion history, cultural studies" },
      { name: "Cooking", description: "Indian cuisine, fusion, improvisation" },
      { name: "Sports", description: "Pistol shooting, badminton, skating, woodball (national title)" },
      { name: "Styling", description: "Visual editing, texture & silhouette work" },
      { name: "Ideation", description: "Structured chaos, conceptual thinking" },
    ],

    pillars: [
      {
        name: "Cultural Foresight Systems",
        description: "Decode global signals, collector behaviour, and heritage codes so maisons enter every market already fluent.",
      },
      {
        name: "Narrative Immersion Engines",
        description: "Design cinematic story arcs that connect runway, retail, and digital touchpoints with one emotional through line.",
      },
      {
        name: "Merchandising Intelligence Lab",
        description: "Fuse demand data with creative direction to engineer assortments that feel inevitable and sell-through ready.",
      },
      {
        name: "Collector Ecosystem Design",
        description: "Build private client rituals and membership worlds that deepen devotion long after a launch.",
      },
    ],
  };

  // Prepare context for AI
  const getContextString = () => {
    return `
Name: ${knowledgeBase.name}
Title: ${knowledgeBase.title}
Summary: ${knowledgeBase.summary}

Expertise:
${knowledgeBase.expertise.map(skill => `- ${skill}`).join('\n')}

Professional Experience:
${knowledgeBase.professional.map(exp => `
${exp.companyAndRole}
Timeline: ${exp.timeline}
Highlights: ${exp.highlights ? exp.highlights.join(', ') : 'N/A'}
`).join('\n')}

Projects:
${knowledgeBase.projects.map(proj => `
Project: ${proj.name}
Industry: ${proj.industry}
Description: ${proj.description}
`).join('\n')}

Hobbies and Interests:
${knowledgeBase.hobbies.map(h => `- ${h.name}: ${h.description}`).join('\n')}

Her 4 Pillars of Work:
${knowledgeBase.pillars.map(p => `- ${p.name}: ${p.description}`).join('\n')}
`;
  };

  // AI Response Generator using Groq API
  const generateResponse = async (userMessage) => {
    try {
      const contextString = getContextString();
      const systemPrompt = getMeemoSystemPrompt(contextString);

      const chatCompletion = await groqClient.chat.completions.create({
        messages: [
          {
            role: "system",
            content: systemPrompt
          },
          {
            role: "user",
            content: userMessage
          }
        ],
        model: "llama-3.1-8b-instant",
        temperature: 0.7,
        max_tokens: 500,
      });

      return chatCompletion.choices[0].message.content;
    } catch (error) {
      console.error('Error calling AI:', error);
      return "I apologize, but I'm having trouble connecting to my AI service right now. Please try again in a moment.";
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      type: "user",
      content: inputValue,
      timestamp: new Date(),
    };

    const messageToSend = inputValue;
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsTyping(true);

    // Get AI response
    const aiResponse = await generateResponse(messageToSend);

    // Check for special actions
    if (aiResponse.includes("[ACTION:CONTACT]")) {
      const cleanedContent = aiResponse.replace("[ACTION:CONTACT]", "").trim();
      const botResponse = {
        type: "bot",
        content: cleanedContent,
        action: "contact",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botResponse]);
      setShowContactForm(true);
    } else if (aiResponse.includes("[ACTION:RESUME]")) {
      const cleanedContent = aiResponse.replace("[ACTION:RESUME]", "").trim();
      const botResponse = {
        type: "bot",
        content: cleanedContent,
        action: "resume",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botResponse]);
    } else {
      const botResponse = {
        type: "bot",
        content: aiResponse,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botResponse]);
    }

    setIsTyping(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    // Initialize EmailJS (you'll need to replace these with your EmailJS credentials)
    // Get your credentials from https://www.emailjs.com/
    try {
      await emailjs.send(
        'YOUR_SERVICE_ID',  // Replace with your EmailJS service ID
        'YOUR_TEMPLATE_ID', // Replace with your EmailJS template ID
        {
          from_name: contactDetails.name,
          from_email: contactDetails.email,
          message: contactDetails.message,
          to_name: 'Surmayee Umathe',
        },
        'YOUR_PUBLIC_KEY' // Replace with your EmailJS public key
      );

      // Show success message
      const successMessage = {
        type: "bot",
        content: `Thanks ${contactDetails.name}! Your message has been sent to Surmayee. She'll get back to you at ${contactDetails.email} soon!`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, successMessage]);

      // Reset form and hide it
      setContactDetails({ name: "", email: "", message: "" });
      setShowContactForm(false);
    } catch (error) {
      console.error('Email error:', error);
      const errorMessage = {
        type: "bot",
        content: "Oops! There was an error sending your message. Please try again or email directly at surmayee@email.com",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  };

  const suggestedQuestions = [
    "What is her experience in merchandising?",
    "Tell me about her skills",
    "What are her hobbies?",
    "Has she worked at Nykaa?",
  ];

  const handleSuggestionClick = (question) => {
    setInputValue(question);
    inputRef.current?.focus();
  };

  return (
    <div className="askbot-page">
      <div className="askbot-background-orbs">
        <motion.div
          className="askbot-orb askbot-orb--1"
          animate={{
            x: [0, 50, -30, 0],
            y: [0, -40, 30, 0],
            scale: [1, 1.1, 0.9, 1],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="askbot-orb askbot-orb--2"
          animate={{
            x: [0, -50, 40, 0],
            y: [0, 50, -30, 0],
            scale: [1, 0.9, 1.1, 1],
          }}
          transition={{
            duration: 14,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="askbot-orb askbot-orb--3"
          animate={{
            x: [0, 30, -20, 0],
            y: [0, -20, 40, 0],
            scale: [1, 1.15, 0.95, 1],
          }}
          transition={{
            duration: 16,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="askbot-particles">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="askbot-particle"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: 0,
            }}
            animate={{
              y: [null, Math.random() * window.innerHeight],
              opacity: [0, Math.random() * 0.5, 0],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              ease: "linear",
              delay: Math.random() * 5,
            }}
          />
        ))}
      </div>

      <Navigation nightMode={nightMode} />

      <div className="askbot-container">
        <motion.div
          className="askbot-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="askbot-header__title">
            Ask <span className="askbot-gradient-text">Meemo</span>
          </h1>
          <p className="askbot-header__subtitle">
            Your AI companion who knows all about Surmayee's expertise, experience, and creative journey
          </p>
        </motion.div>

        <motion.div
          className="askbot-chat"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="askbot-messages">
            <AnimatePresence>
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  className={`askbot-message ${message.type === "user" ? "askbot-message--user" : "askbot-message--bot"}`}
                  initial={{
                    opacity: 0,
                    y: 20,
                    scale: 0.9,
                    filter: "blur(4px)"
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    filter: "blur(0px)"
                  }}
                  transition={{
                    duration: 0.5,
                    type: "spring",
                    stiffness: 100,
                    damping: 15
                  }}
                  whileHover={{
                    scale: 1.01,
                    transition: { duration: 0.2 }
                  }}
                >
                  <motion.div
                    className="askbot-message__avatar"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{
                      delay: 0.1,
                      type: "spring",
                      stiffness: 200,
                      damping: 15
                    }}
                  >
                    {message.type === "user" ? (
                      <User size={18} strokeWidth={2} />
                    ) : (
                      <Bot size={18} strokeWidth={2} />
                    )}
                  </motion.div>
                  <div className="askbot-message__content">
                    <motion.div
                      className="askbot-message__text"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.4 }}
                    >
                      {message.content}
                    </motion.div>
                    {message.action === "resume" && (
                      <motion.a
                        href="/resume.pdf"
                        download="Surmayee_Umathe_Resume.pdf"
                        className="askbot-action-button"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          marginTop: "12px",
                          padding: "10px 16px",
                          background: "var(--accent-color, #ff6ba9)",
                          color: "white",
                          borderRadius: "8px",
                          textDecoration: "none",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <Download size={16} />
                        Download Resume
                      </motion.a>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {isTyping && (
              <motion.div
                className="askbot-message askbot-message--bot"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="askbot-message__avatar">
                  <Bot size={18} strokeWidth={2} />
                </div>
                <div className="askbot-message__content">
                  <div className="askbot-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {messages.length === 1 && (
            <div className="askbot-suggestions">
              <p className="askbot-suggestions__label">Try asking:</p>
              <div className="askbot-suggestions__grid">
                {suggestedQuestions.map((question, index) => (
                  <motion.button
                    key={index}
                    className="askbot-suggestion"
                    onClick={() => handleSuggestionClick(question)}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {question}
                  </motion.button>
                ))}
              </div>
            </div>
          )}

          {showContactForm && (
            <motion.div
              className="askbot-contact-form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                background: "rgba(255, 107, 169, 0.1)",
                borderRadius: "12px",
                padding: "20px",
                margin: "16px 0",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                <Mail size={20} style={{ color: "#ff6ba9" }} />
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "600" }}>Get in Touch</h3>
              </div>
              <form onSubmit={handleContactSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <input
                  type="text"
                  placeholder="Your Name"
                  value={contactDetails.name}
                  onChange={(e) => setContactDetails({ ...contactDetails, name: e.target.value })}
                  required
                  style={{
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid rgba(255, 107, 169, 0.3)",
                    fontSize: "14px",
                    outline: "none",
                  }}
                />
                <input
                  type="email"
                  placeholder="Your Email"
                  value={contactDetails.email}
                  onChange={(e) => setContactDetails({ ...contactDetails, email: e.target.value })}
                  required
                  style={{
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid rgba(255, 107, 169, 0.3)",
                    fontSize: "14px",
                    outline: "none",
                  }}
                />
                <textarea
                  placeholder="Your Message"
                  value={contactDetails.message}
                  onChange={(e) => setContactDetails({ ...contactDetails, message: e.target.value })}
                  required
                  rows="4"
                  style={{
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid rgba(255, 107, 169, 0.3)",
                    fontSize: "14px",
                    outline: "none",
                    resize: "vertical",
                  }}
                />
                <div style={{ display: "flex", gap: "8px" }}>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      flex: 1,
                      padding: "12px",
                      background: "#ff6ba9",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                    }}
                  >
                    Send Message
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => setShowContactForm(false)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      padding: "12px 20px",
                      background: "transparent",
                      color: "#ff6ba9",
                      border: "1px solid #ff6ba9",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </motion.button>
                </div>
              </form>
            </motion.div>
          )}

          <div className="askbot-input-container">
            <div className="askbot-input-wrapper">
              <input
                ref={inputRef}
                type="text"
                className="askbot-input"
                placeholder="Ask anything about Surmayee..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <motion.button
                className="askbot-send"
                onClick={handleSend}
                disabled={!inputValue.trim()}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Send size={20} strokeWidth={2} />
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>

      <Footer nightMode={nightMode} />
    </div>
  );
};

export default AskBotPage;
